/**
 * Human labels for policy completeness paths (API / missing_required).
 * Keep AL_PACK_REQUIRED_TOTAL / HC_PACK_REQUIRED_TOTAL_BASE in sync with backend.
 */

import { policyProfileSectionAnchor } from "./profileSectionAnchors";

/** Cardinality when every required path is blank (AL pack). Fallback if API omits required_total. */
export const AL_PACK_REQUIRED_TOTAL = 38;

/** HC base when medication_management is not True (no med-storage trio). */
export const HC_PACK_REQUIRED_TOTAL_BASE = 35;

/** Extra HC fields when medication_management is True. */
export const HC_PACK_MED_STORAGE_TOTAL = 3;

/** GH: no service triad / med storage / infection role / TB / employee health; + hcbs_support_level. */
export const GH_PACK_REQUIRED_TOTAL = 27;

/** Hospice: AL minus service triad and med storage; keeps roles + TB ops; + medical_director (3 fields). */
export const HOSPICE_PACK_REQUIRED_TOTAL = 34;

export function packRequiredTotalFallback(
  licenseFamily?: string | null,
  medicationManagement?: boolean | null
): number {
  const family = (licenseFamily || "").trim();
  if (family === "home_care") {
    return (
      HC_PACK_REQUIRED_TOTAL_BASE +
      (medicationManagement === true ? HC_PACK_MED_STORAGE_TOTAL : 0)
    );
  }
  if (family === "group_home") {
    return GH_PACK_REQUIRED_TOTAL;
  }
  if (family === "hospice") {
    return HOSPICE_PACK_REQUIRED_TOTAL;
  }
  return AL_PACK_REQUIRED_TOTAL;
}

/**
 * Lowercase license-family name for prose ("personalizes the <X> package").
 * Never defaults to assisted living — unknown families return null so callers
 * can fall back to neutral wording instead of mislabelling an HC/GH/hospice pack.
 */
export function packFamilyLabel(family?: string | null): string | null {
  switch ((family || "").trim().toLowerCase()) {
    case "assisted_living":
      return "assisted living";
    case "home_care":
      return "home care";
    case "group_home":
      return "group home";
    case "hospice":
      return "hospice";
    default:
      return null;
  }
}

/** Storage keys for statute designates (unchanged across license families). */
export type PolicyPersonRoleKey =
  | "assisted_living_director"
  | "clinical_nurse_supervisor"
  | "privacy_officer"
  | "infection_preventionist"
  | "medical_director";

export type PersonRoleLabelOptions = {
  /** From policy_pack.license_family (or facility). */
  licenseFamily?: string | null;
  /** When false / deferred / unknown, use generic Director / Clinical lead. */
  packAvailable?: boolean | null;
};

const SHARED_ROLE_LABELS: Record<
  "privacy_officer" | "infection_preventionist" | "medical_director",
  string
> = {
  privacy_officer: "Privacy officer",
  infection_preventionist: "Infection preventionist",
  medical_director: "Medical director",
};

/**
 * License-aware UI labels for policy designate roles.
 * Storage keys stay `assisted_living_director` / `clinical_nurse_supervisor`.
 * `medical_director` is hospice-only in the profile UI (§ 144A.753 subd. 2(2)).
 */
export function personRoleLabels(
  opts: PersonRoleLabelOptions = {}
): Record<PolicyPersonRoleKey, string> {
  const available = opts.packAvailable === true;
  const family = (opts.licenseFamily || "").trim().toLowerCase();

  if (!available || !family || family === "unknown") {
    return {
      assisted_living_director: "Director",
      clinical_nurse_supervisor: "Clinical lead",
      ...SHARED_ROLE_LABELS,
    };
  }

  if (family === "assisted_living") {
    return {
      assisted_living_director: "Assisted living director",
      clinical_nurse_supervisor: "Clinical nurse supervisor",
      ...SHARED_ROLE_LABELS,
    };
  }

  if (family === "home_care") {
    return {
      assisted_living_director: "Home care director",
      clinical_nurse_supervisor: "Clinical nurse supervisor",
      ...SHARED_ROLE_LABELS,
    };
  }

  if (family === "group_home") {
    return {
      assisted_living_director: "Program designate / director",
      clinical_nurse_supervisor: "Clinical lead",
      ...SHARED_ROLE_LABELS,
    };
  }

  if (family === "hospice") {
    return {
      assisted_living_director: "Hospice administrator / director",
      clinical_nurse_supervisor: "Clinical nurse supervisor",
      ...SHARED_ROLE_LABELS,
    };
  }

  // Deferred / other families: avoid AL-specific wording.
  return {
    assisted_living_director: "Director",
    clinical_nurse_supervisor: "Clinical lead",
    ...SHARED_ROLE_LABELS,
  };
}

/** Default AL pack labels (backward-compatible callers without pack context). */
const ROLE_LABELS: Record<string, string> = personRoleLabels({
  licenseFamily: "assisted_living",
  packAvailable: true,
});

const FIELD_LABELS: Record<string, string> = {
  "facility.legal_name": "Legal name",
  "facility.short_name": "Short name",
  "facility.license_number": "License number",
  "facility.license_type": "License type",
  "facility.main_phone": "Main phone",
  "facility.clinical_on_call_number": "Clinical on-call number",
  "facility.emergency_contact_location": "Emergency contact location",
  "facility.employee_health_provider": "Employee health provider",
  "facility.tb_referral_process": "TB referral process",
  "facility.home_care_license_level": "Home care license level",
  "facility.hcbs_support_level": "HCBS support level (basic vs intensive)",
  "facility.address.line1": "Street address",
  "facility.address.city": "City",
  "facility.address.state": "State",
  "facility.address.postal_code": "ZIP code",
  "services.medication_management": "Medication management",
  "services.treatment_therapy_management": "Treatment / therapy",
  "services.medication_treatment_exercise_reminders":
    "Medication / treatment / exercise reminders",
  "services.medication_management_requires_comprehensive":
    "Medication management requires a comprehensive home care license",
  "services.treatment_therapy_management_requires_comprehensive":
    "Treatment / therapy requires a comprehensive home care license",
  "services.memory_care": "Memory care",
  "services.respite": "Respite",
  "medication_storage.audit_frequency": "Audit frequency",
  "medication_storage.controlled_substance_security_standard":
    "Controlled substance security",
  "medication_storage.refrigerator_monitoring_procedure":
    "Refrigerator monitoring procedure",
  "governance.policy_owner": "Policy owner",
  "governance.approver_role": "Approver role",
  "governance.review_frequency": "Review frequency",
  "governance.effective_date": "Effective date",
  "governance.review_date": "Review date",
  "governance.version": "Document version",
};

/** HC-facing labels for the same medication_storage.* profile keys (§144A.4792 ops). */
const HC_MED_STORAGE_LABELS: Record<string, string> = {
  "medication_storage.audit_frequency":
    "Medication-record audit frequency",
  "medication_storage.controlled_substance_security_standard":
    "Controlled-substance security (when Provider stores)",
  "medication_storage.refrigerator_monitoring_procedure":
    "Refrigerated-medication monitoring",
};

const PERSON_FIELD: Record<string, string> = {
  name: "name",
  phone: "phone",
  email: "email",
  title: "title",
};

/** Plain-language label for a missing_required path. */
export function missingFieldLabel(
  path: string,
  opts?: PersonRoleLabelOptions
): string {
  if (
    opts?.licenseFamily === "home_care" &&
    HC_MED_STORAGE_LABELS[path]
  ) {
    return HC_MED_STORAGE_LABELS[path];
  }
  const known = FIELD_LABELS[path];
  if (known) return known;

  const roleMatch = path.match(
    /^facility\.(assisted_living_director|clinical_nurse_supervisor|privacy_officer|infection_preventionist|medical_director)\.(name|phone|email|title)$/
  );
  if (roleMatch) {
    const labels = opts ? personRoleLabels(opts) : ROLE_LABELS;
    const role = labels[roleMatch[1] as PolicyPersonRoleKey] || roleMatch[1];
    const field = PERSON_FIELD[roleMatch[2]] || roleMatch[2];
    return `${role} · ${field}`;
  }

  const tail = path.split(".").pop() || path;
  return tail.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Label for an unresolved template token (e.g. policy_owner, facility.legal_name).
 * Cover-sheet aliases map to Profile → Governance fields.
 */
export function unresolvedPlaceholderLabel(
  token: string,
  opts?: PersonRoleLabelOptions
): string {
  const COVER: Record<string, string> = {
    policy_owner: "Policy owner (Profile → Governance)",
    approver_role: "Approver role (Profile → Governance)",
    effective_date: "Effective date (Profile → Governance)",
    review_date: "Review date (Profile → Governance)",
    version: "Document version (Profile → Governance)",
  };
  if (COVER[token]) return COVER[token];
  if (token.startsWith("governance.")) {
    return `${missingFieldLabel(token, opts)} (Profile → Governance)`;
  }
  if (
    token.startsWith("facility.") ||
    token.startsWith("services.") ||
    token.startsWith("medication_storage.")
  ) {
    return missingFieldLabel(token, opts);
  }
  return missingFieldLabel(token, opts);
}

/** Profile section id for an unresolved token or completeness path. */
export function sectionForPlaceholderOrPath(tokenOrPath: string): string {
  if (
    tokenOrPath === "policy_owner" ||
    tokenOrPath === "approver_role" ||
    tokenOrPath === "effective_date" ||
    tokenOrPath === "review_date" ||
    tokenOrPath === "version" ||
    tokenOrPath.startsWith("governance.")
  ) {
    return "policy-governance";
  }
  return policyProfileSectionAnchor(
    tokenOrPath.includes(".") ? tokenOrPath : `facility.${tokenOrPath}`
  );
}

export function formatLicenseTypeLabel(raw: string | null | undefined): string {
  const t = (raw || "").trim();
  if (!t) return "";
  if (t.includes(" ")) return t;
  return t
    .split("_")
    .filter(Boolean)
    .map((w, i) =>
      i === 0
        ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        : w.toLowerCase()
    )
    .join(" ");
}

/** Prefer an unresolved/missing role path so deep-links match the pack in use. */
export function firstRoleContactPath(
  unresolved: string[] | undefined,
  missing: string[]
): string {
  const ROLE_CONTACT_PATH_RE =
    /^facility\.(assisted_living_director|clinical_nurse_supervisor|privacy_officer|infection_preventionist|medical_director)\./;
  for (const token of unresolved ?? []) {
    const path = token.includes(".") ? token : `facility.${token}`;
    if (
      ROLE_CONTACT_PATH_RE.test(path) ||
      sectionForPlaceholderOrPath(token) === "policy-roles"
    ) {
      return path;
    }
  }
  const fromMissing = missing.find((p) => ROLE_CONTACT_PATH_RE.test(p));
  return fromMissing ?? "facility.assisted_living_director.name";
}

export function firstGovernancePath(
  unresolved: string[] | undefined,
  missing: string[]
): string {
  for (const token of unresolved ?? []) {
    if (sectionForPlaceholderOrPath(token) === "policy-governance") {
      if (token.startsWith("governance.")) return token;
      if (token.includes(".")) return token;
      return `governance.${token}`;
    }
  }
  const fromMissing = missing.find(
    (p) => sectionForPlaceholderOrPath(p) === "policy-governance"
  );
  return fromMissing ?? "governance.policy_owner";
}

export function profileProgress(
  missingCount: number,
  requiredTotal: number = AL_PACK_REQUIRED_TOTAL
): {
  done: number;
  total: number;
  percent: number;
} {
  // Expand denominator if API reports more missing than the known catalog total.
  const total = Math.max(1, requiredTotal, missingCount);
  const done = Math.max(0, Math.min(total, total - missingCount));
  const percent = Math.round((done / total) * 100);
  return { done, total, percent };
}

export const SECTION_ORDER = [
  "policy-identity",
  "policy-roles",
  "policy-operations",
  "policy-services",
  "policy-medication-storage",
  "policy-governance",
] as const;

export type ProfileSectionId = (typeof SECTION_ORDER)[number];
